import * as _ from "lodash-es";
import EcommerceCardFieldEnum from "../models/EcommerceCardFieldEnum";
import ISupplierSettings from "../models/ISupplierSettings";

export type ICardViewSettings = NonNullable<NonNullable<NonNullable<ISupplierSettings["screens"]>["eCommerce"]>["cardView"]>;

export interface INormalizedCardView {
    fieldOrder: EcommerceCardFieldEnum[];
    visibility: Record<EcommerceCardFieldEnum, boolean>;
}

/**
 * Normalizes the persisted eCommerce cardView settings (legacy boolean-only or current
 * fieldOrder-based) into a single ordered representation, and denormalizes it back to the
 * persisted shape. Persisted saves dual-write fieldOrder AND the *Visible booleans so readers
 * that only understand the booleans keep working.
 */
export default class EcommerceCardViewService {
    // The pre-fieldOrder hard-coded render order of EcommerceListView; PRICE then ACTIONS
    // last so the price defaults to sitting adjacent to the qty/add-to-cart controls
    static readonly DEFAULT_CARD_FIELD_ORDER: EcommerceCardFieldEnum[] = [
        EcommerceCardFieldEnum.NAME,
        EcommerceCardFieldEnum.SKU,
        EcommerceCardFieldEnum.MSRP,
        EcommerceCardFieldEnum.SHORT_DESCRIPTION,
        EcommerceCardFieldEnum.LONG_DESCRIPTION,
        EcommerceCardFieldEnum.CATEGORY,
        EcommerceCardFieldEnum.PRICE,
        EcommerceCardFieldEnum.ACTIONS,
    ];

    static visibleKey(field: EcommerceCardFieldEnum): keyof ICardViewSettings {
        return `${field}Visible` as keyof ICardViewSettings;
    }

    static normalizeCardView(cardView?: ICardViewSettings): INormalizedCardView {
        const visibility = {} as Record<EcommerceCardFieldEnum, boolean>;

        for (const field of EcommerceCardViewService.DEFAULT_CARD_FIELD_ORDER) {
            const visibleValue = cardView?.[EcommerceCardViewService.visibleKey(field)];

            visibility[field] = _.isNil(visibleValue) ? true : !!visibleValue;
        }

        // The purchase controls can be repositioned but never hidden
        visibility[EcommerceCardFieldEnum.ACTIONS] = true;

        const rawFieldOrder = _.isArray(cardView?.fieldOrder) ? cardView!.fieldOrder! : [];

        // Drop unknown keys and duplicates, then append any missing fields in default order
        const fieldOrder = rawFieldOrder.filter(
            (field, index) => EcommerceCardViewService.DEFAULT_CARD_FIELD_ORDER.includes(field) && rawFieldOrder.indexOf(field) === index
        );

        for (const field of EcommerceCardViewService.DEFAULT_CARD_FIELD_ORDER) {
            if (!fieldOrder.includes(field)) {
                fieldOrder.push(field);
            }
        }

        return { fieldOrder, visibility };
    }

    static denormalizeCardView(normalized: INormalizedCardView): ICardViewSettings {
        return {
            fieldOrder: [...normalized.fieldOrder],
            nameVisible: normalized.visibility[EcommerceCardFieldEnum.NAME],
            skuVisible: normalized.visibility[EcommerceCardFieldEnum.SKU],
            msrpVisible: normalized.visibility[EcommerceCardFieldEnum.MSRP],
            priceVisible: normalized.visibility[EcommerceCardFieldEnum.PRICE],
            shortDescriptionVisible: normalized.visibility[EcommerceCardFieldEnum.SHORT_DESCRIPTION],
            longDescriptionVisible: normalized.visibility[EcommerceCardFieldEnum.LONG_DESCRIPTION],
            categoryVisible: normalized.visibility[EcommerceCardFieldEnum.CATEGORY],
        };
    }
}
